/**
 * bookingSite.ts — the public booking site, and the terms a patient agrees to,
 * as constants.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: the checkout terms link
 * resolves (2026-09-04, issue #308).
 *
 * WHY THE TERMS URL IS A CONSTANT AND NOT A LITERAL AT EACH CALL SITE — the
 * same argument as `safety/route.ts`, and this repo has already lost it once.
 * Four places have to agree on this address: the legal footer, and the three
 * surfaces that render `checkout/CommitmentTerms` (checkout, the emailed
 * end-of-term re-subscribe at /resubscribe, and the in-portal re-subscribe
 * card). The footer had the real URL. `CommitmentTerms` had `'/terms'` — a path
 * this portal has never served — so the link fell through to `/:clinicSlug` and
 * the patient, who had just been REQUIRED to tick "I agree to the terms and
 * conditions" in order to pay, landed on "We can't find that clinic". Two
 * literals, one of them wrong, on the one screen where somebody commits money.
 *
 * IT LOOKED FINE FROM THE OUTSIDE, which is why it survived three call sites.
 * nginx serves index.html for every path (the SPA fallback), so the dead link
 * answered 200 and rendered a page — nothing an uptime probe or a link checker
 * looks at was wrong. The only place the defect is visible is the route table,
 * which is why `checkout/termsLink.spec.tsx` asserts against `routes.tsx`
 * rather than against a hard-coded expected string.
 *
 * DO NOT ADD `{ path: '/terms', element: <Navigate …/> }` NEXT TIME. It buys
 * nothing over pointing the href at the page that exists, and it costs: a
 * second address for the terms to be found at, a line in `routes.tsx`, and a
 * reserved-segment decision in `tracking/gtm.ts` for every reader after us. The
 * terms are published and maintained on the booking site; link to them there.
 *
 * ⚠️ THIS MAKES THE LINK RESOLVE. IT DOES NOT MAKE THE TERMS RIGHT. Whether the
 * marketplace terms actually cover a LOCKED three-month prescription
 * subscription — the CCRs 2013 cooling-off right, the DMCCA 2024 reminder duty —
 * is the open legal question in `CommitmentTerms`'s own header, and nobody has
 * answered it. A resolving link to terms that may not describe this contract is
 * a better defect than a dead one; it is not a closed one.
 */

/**
 * The public booking site, for everything in the PORTAL bundle that addresses
 * it: the footer's five legal links and the allow-list a notify-widget URL must
 * match in `admin/NotifyRequestsTab`. Both used to keep their own copy of the
 * host, which is one edit and one missed edit away from a footer pointing at a
 * domain we have left.
 *
 * IT DOES NOT REACH THE STANDALONE WIDGETS. `embed/` and `notify-widget/` are
 * their own vite builds, shipped to the marketing site and deliberately kept
 * free of imports out of the app, so they still carry the literal. Do not
 * "finish the job" by importing this there — the coupling costs more than the
 * duplication, and `tracking/ConsentBanner`'s cookie-policy URL is a separate
 * tidy-up nobody has asked for.
 */
export const BOOKING_ORIGIN = 'https://booking.thegpservice.co.uk';

/**
 * The terms of business a patient ticks to agree to before paying.
 *
 * ABSOLUTE AND OFF-ORIGIN ON PURPOSE. The terms live on the booking site, where
 * they are written and kept current; this portal deliberately publishes no copy
 * of them (see above). Reachable when this shipped — and it is the property the
 * spec pins, not the wording behind it.
 */
export const TERMS_URL = `${BOOKING_ORIGIN}/marketplace-terms`;

/**
 * The privacy notice — the footer's link, now with a second call site.
 *
 * It became a constant (2026-09-11) for the same reason `TERMS_URL` did: the
 * marketing-consent boxes (`marketing/MarketingConsentFields`) point a patient at
 * the notice at the moment they are asked for consent, and a second literal
 * beside the footer's is one edit away from a consent screen linking to a notice
 * we have moved. The address is the footer's, unchanged — not a new one.
 */
export const PRIVACY_URL = `${BOOKING_ORIGIN}/marketplace-privacy-policy`;
